import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface StepperStep {
  label: string;
  description?: string;
}

export interface StepperProps {
  steps: StepperStep[];
  currentStep: number;
  onStepClick?: (step: number) => void;
  orientation?: 'horizontal' | 'vertical';
  className?: string;
}

/**
 * Stepper Component für Multi-Step Flows (Wizard, Onboarding, etc).
 *
 * @example
 * <Stepper
 *   steps={[
 *     { label: 'Format', description: 'Wähle Bildformat' },
 *     { label: 'Dimensionen' },
 *     { label: 'Upscale' },
 *     { label: 'Preview' },
 *   ]}
 *   currentStep={2}
 *   onStepClick={(step) => setCurrentStep(step)}
 * />
 */
export function Stepper({
  steps,
  currentStep,
  onStepClick,
  orientation = 'horizontal',
  className,
}: StepperProps) {
  const { t } = useTranslation();
  return (
    <ol
      className={cn(
        'flex',
        orientation === 'vertical' ? 'flex-col space-y-4' : 'space-x-2',
        className
      )}
      role="list"
      aria-label={t('a11y.progressSteps')}
    >
      {steps.map((step, index) => {
        const isCompleted = index < currentStep;
        const isCurrent = index === currentStep;
        const isUpcoming = index > currentStep;
        const isClickable = onStepClick && !isUpcoming;

        return (
          <li key={index} className="flex items-center flex-1">
            <button
              type="button"
              onClick={() => isClickable && onStepClick(index)}
              disabled={!isClickable}
              className={cn(
                'flex items-center gap-2 transition-colors',
                isClickable && 'cursor-pointer hover:opacity-80',
                !isClickable && 'cursor-default'
              )}
              aria-current={isCurrent ? 'step' : undefined}
              aria-disabled={!isClickable}
            >
              {/* Circle */}
              <span
                className={cn(
                  'flex h-10 w-10 items-center justify-center rounded-full border-2 transition-all',
                  isCurrent &&
                    'border-primary bg-primary text-primary-foreground font-semibold shadow-sm',
                  isCompleted &&
                    'border-success bg-success text-success-foreground',
                  isUpcoming && 'border-muted bg-muted text-muted-foreground'
                )}
                aria-label={
                  isCompleted
                    ? `${step.label} completed`
                    : isCurrent
                    ? `${step.label} current`
                    : `${step.label} upcoming`
                }
              >
                {isCompleted ? (
                  <Check className="h-5 w-5" aria-hidden="true" />
                ) : (
                  <span className="text-sm">{index + 1}</span>
                )}
              </span>

              {/* Label & Description */}
              <div className="flex flex-col text-left">
                <span
                  className={cn(
                    'text-sm font-medium',
                    isCurrent && 'text-foreground',
                    isCompleted && 'text-muted-foreground',
                    isUpcoming && 'text-muted-foreground opacity-60'
                  )}
                >
                  {step.label}
                </span>
                {step.description && (
                  <span className="text-xs text-muted-foreground">
                    {step.description}
                  </span>
                )}
              </div>
            </button>

            {/* Separator (except last step) */}
            {index < steps.length - 1 && orientation === 'horizontal' && (
              <div
                className={cn(
                  'h-0.5 flex-1 mx-2 transition-colors',
                  isCompleted ? 'bg-success' : 'bg-muted'
                )}
                aria-hidden="true"
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}

Stepper.displayName = 'Stepper';
